import { FC, Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { Formik } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import { RegistrationPayment } from "@/app/registration-payments/registration-payments";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import TableListMatricula from "@/shared/components/organisms/TableListMatricula";
import { TableListCourses } from "@/shared/components/organisms/TableListCourses";
import TableListFestivos from "@/shared/components/organisms/TableListFestivos";
import { InputDropDownSpan } from "@/shared/components/atoms/InputDropDownSpan";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import TableListGrupos from "@/shared/components/organisms/TableListGrupos";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { SelectAvatar } from "@/shared/components/organisms/SelectAvatar";
import { DateRangePicker } from "@/shared/components/atoms/DataRangePicker";
import { CheckBoxDays } from "@/shared/components/atoms/CheckBoxDays";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { ModalXXL } from "@/shared/components/organisms/ModalXXL";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { Select } from "@/shared/components/organisms/Select";
import { Modal } from "@/shared/components/organisms/Modal";
import { toasts } from "@/shared/utils/toasts";

import { useCreateReasonMutation } from "@/app/reasons/reasons.api";
import {
  createReasonInitialValue,
  createReasonValidationSchema,
  CreateReasonValues,
} from "@/shared/schemas/reasons.schema";
import { reasonTypes } from "@/app/reasons/reasons";
import {
  createRegistrationPaymentInitialValues,
  createRegistrationPaymentValidationSchema,
} from "@/shared/schemas/registration-payments.schema";
import {
  CourseValues,
  courseInitialValues,
  courseValidationSchema,
} from "@/shared/schemas/course.schema";
import {
  useCoursesFeeQuery,
  useCoursesQuery,
  useCreateCourseMutation,
  useCreatePreviusAccessTimeMutation,
  useDeleteCourseMutation,
  useDeleteCourseUserMutation,
  useGetCourseUsersQuery,
  useUpdateCourseMutation,
} from "@/app/courses/courses.api";
import {
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetListGroupsQuery,
  useSlotsPlacesQuery,
  useUpdateGroupMutation,
} from "@/app/slots/slots.api";
import {
  useRegistrationPaymentsQuery,
  useCreateRegistrationPaymentMutation,
  useUpdateRegistrationPaymentMutation,
} from "@/app/registration-payments/registration-payments.api";

import {
  holidaysInitialValues,
  holidaysValidationSchema,
} from "@/shared/schemas/lessons.schema";
import { useSpecialistsQuery } from "@/app/branchs/branchs.api";

import { colors, currencies, Fee } from "@/app/fees/fees";

import { useBondsQuery, useCreateBondMutation } from "@/app/bonds/bonds.api";

import {
  useCreateHolidayMutation,
  useDeleteHolidayMutation,
  useHolidaysQuery,
  useUpdateHolidayMutation,
} from "@/app/holidays/holidays.api";
import { useDebounce } from "@/shared/hooks/debounce";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import { Empty } from "@/shared/components/organisms/Empty";
import { Trash2Icon } from "lucide-react";
import { FIELD_REQUIRED_MESSAGE } from "@/shared/utils/validations";
import { SelectColor } from "@/shared/components/organisms/SelectColor";
import { PlusCircleIcon } from "@heroicons/react/20/solid";

const optionsPeriodo = [
  { id: 1, name: "Semanal" },
  { id: 2, name: "Quincenal" },
  { id: 3, name: "Mensual" },
  { id: 4, name: "Bimensual" },
  { id: 5, name: "Trimestral" },
];

const optionSelectFestivos = [
  { id: 1, name: "Local" },
  { id: 2, name: "Provincial" },
  { id: 3, name: "Nacional" },
  { id: 4, name: "Mundial" },
];

export const Courses: FC<any> = () => {
  const { id } = useParams();
  const [search, setSearch] = useState<string | undefined>();
  const [editCurrentCourse, setEditCurrentCourse] = useState<Fee | undefined>();
  const [currentRegistrationPaymentEdit, setCurrentRegistrationPaymentEdit] =
    useState<RegistrationPayment | undefined>();
  const [currentEventEdit, setCurrentEventEdit] = useState<undefined>();
  const [viewUserCourserId, setViewUserCourserId] = useState<number | null>(
    null
  );
  const slotsPlacesQuery = useSlotsPlacesQuery({ branchId: id });
  const specialistsQuery = useSpecialistsQuery({ branchId: id });
  const listGroupsQuery = useGetListGroupsQuery({ branchId: id });
  const [createGroup, createGroupResult] = useCreateGroupMutation();
  const [updateGroup, updateGroupResult] = useUpdateGroupMutation();
  const [createReason, createReasonResult] = useCreateReasonMutation();
  const [deleteGroup, deleteGroupResult] = useDeleteGroupMutation();

  const registrationPaymentsQuery = useRegistrationPaymentsQuery({
    branchId: id,
  });

  const [createRegistrationPayment, createRegistrationPaymentResult] =
    useCreateRegistrationPaymentMutation();
  const [updateRegistrationPayment, updateRegistrationPaymentResult] =
    useUpdateRegistrationPaymentMutation();
  const debouncedSearch = useDebounce(search, 200);
  const coursesQuery = useCoursesQuery({
    branchId: id,
    search: debouncedSearch,
  });
  const courseUsersQuery = useGetCourseUsersQuery(
    {
      branchId: id,
      courseId: viewUserCourserId,
    },
    {
      skip: !viewUserCourserId,
      refetchOnMountOrArgChange: true, // Refetch cuando el componente se monta o los argumentos cambian
    }
  );
  const [createPreviusAccessTime, createPreviusAccessTimeResult] =
    useCreatePreviusAccessTimeMutation();

  const [deleteCourseUser, deleteCourseUserResult] =
    useDeleteCourseUserMutation();
  const [createCourse] = useCreateCourseMutation();
  const [updateCourse, updateCourseResult] = useUpdateCourseMutation();
  const coursesFeeQuery = useCoursesFeeQuery({
    branchId: id,
  });
  const [deleteCourse, deleteCourseResult] = useDeleteCourseMutation();
  const [openModalViewUserCourse, setOpenModalViewUserCourse] = useState(false);

  const holidaysQuery = useHolidaysQuery({ branchId: id });
  const [currentHolidayEdit, setCurrentHolidayEdit] = useState<any>();
  const [createHoliday, createHolidayResult] = useCreateHolidayMutation();
  const [updateHoliday, updateHolidayResult] = useUpdateHolidayMutation();
  const [deleteHoliday, deleteHolidayResult] = useDeleteHolidayMutation();

  const functionOpenModalViewUserCourse = () => {
    setOpenModalViewUserCourse(!openModalViewUserCourse);
  };

  useEffect(() => {
    if (createHolidayResult.isSuccess) {
      toasts.success(
        "Festivo creado",
        "El festivo se ha registrado correctamente"
      );
      setOpenDialogFestivos(!openDialogFestivos);
    }
    if (createHolidayResult.isError) {
      toasts.danger(
        "Error al crear",
        "Ha ocurrido un problema al registrar el festivo"
      );
    }
  }, [createHolidayResult]);

  useEffect(() => {
    if (updateHolidayResult.isSuccess) {
      toasts.success(
        "Festivo actualizado",
        "El festivo se ha actualizado correctamente"
      );
      setOpenDialogFestivos(!openDialogFestivos);
    }
    if (updateHolidayResult.isError) {
      toasts.danger(
        "Error al actualizar",
        "Ha ocurrido un problema al actualizar el festivo"
      );
    }
  }, [updateHolidayResult]);

  useEffect(() => {
    if (deleteHolidayResult.isSuccess) {
      toasts.success(
        "Festivo eliminado",
        "El festivo se ha eliminado correctamente"
      );
      setCurrentHolidayEdit(null);
    }
    if (deleteHolidayResult.isError) {
      toasts.danger(
        "Error al eliminar",
        "Ha ocurrido un error al eliminar el festivo"
      );
    }
  }, [deleteHolidayResult]);

  useEffect(() => {
    if (currentHolidayEdit) {
      setOpenDialogFestivos(!openDialogFestivos);
    }
  }, [currentHolidayEdit]);

  const bondsQuery = useBondsQuery({ branchId: id });

  const [createBond, createBondResult] = useCreateBondMutation();

  const [currentRoomEdit, setCurrentRoomEdit] = useState();

  const [open, setOpen] = useState(false);
  const [openDialogEditCourse, setOpenDialogEditCourse] = useState(false);
  const openDialog = () => {
    setOpen(!open);
  };

  const [currentGroupEdit, setCurrentGroupEdit] = useState(null);
  const [currentGroupToDeleteId, setCurrentGroupToDeleteId] = useState<
    number | null
  >(null);
  const [openDeleteGroup, setOpenDeleteGroup] = useState(false);
  const handleOpenDeleteGroup = (id: number) => {
    setCurrentGroupToDeleteId(id);
    setOpenDeleteGroup(!openDeleteGroup);
  };

  useEffect(() => {
    if (currentRegistrationPaymentEdit) {
      setOpenModalCreateMatriculasState(true);
    }
  }, [currentRegistrationPaymentEdit]);

  const [periodo, setPeriodo] = useState(optionsPeriodo[0]);
  //CHECKED ACCESSO APP
  const [accessApp, setAccessApp] = useState(true);
  const handleAccessApp = () => {
    setAccessApp(!accessApp);
  };
  //CHECKED ACCESS PLAN NUTRRITION
  const [accessPlanNutrition, setAccessPlanNutrition] = useState(true);
  const handleAccessPlanNutrition = () => {
    setAccessPlanNutrition(!accessPlanNutrition);
  };
  //CHECKED VISIBLE APP
  const [visibleApp, setVisibleApp] = useState(true);
  const handleVisibleApp = () => {
    setVisibleApp(!visibleApp);
  };
  //CHECKED MULTICENTRO
  const [multicentro, setMulticentro] = useState(false);
  const handleMulticentro = () => {
    setMulticentro(!multicentro);
  };
  //SELECT MULTICENTRO
  const [centro, setCentro] = useState([]);
  //SELECT INSTALACIONES
  const [instalaciones, setInstalaciones] = useState([]);
  //OPEN FOR MODAL MATRICULAS
  const [openModal, setOpenModal] = useState(false);
  const openModalMatriculas = () => {
    setOpenModal(!openModal);
  };
  //OPEN MODAL GRUPOS
  const [openModalGrupos, setOpenModalGrupos] = useState(false);
  const [openModalCreateGrupos, setOpenModalCreateGrupos] = useState(false);

  const functionOpenModalCreateGrupos = () => {
    setOpenModalCreateGrupos(!openModalCreateGrupos);
  };

  const functionOpenModalGrupos = () => {
    setOpenModalGrupos(!openModalGrupos);
  };
  //OPEN DIALOG CREATE GRUPOS
  const [openDialogCreateGrupos, setOpenDialogCreateGrupos] = useState(false);
  const functionOpenDialogCreateGrupos = () => {
    setOpenDialogCreateGrupos(!openDialogCreateGrupos);
  };
  //OPEN DIALOG EDIT GRUPOS
  const [openDialogEditGrupos, setOpenDialogEditGrupos] = useState(false);
  const functionOpenDialogEditGrupos = () => {
    setOpenDialogEditGrupos(!openDialogEditGrupos);
  };
  //OPEN MODAL FESTIVOS
  const [openModalFestivos, setOpenModalFestivos] = useState(false);
  const functionOpenModalFestivos = () => {
    setOpenModalFestivos(!openModalFestivos);
  };
  //OPEN DIALOG FESTIVOS
  const [openDialogFestivos, setOpenDialogFestivos] = useState(false);
  const functionOpenDialogFestivos = () => {
    setOpenDialogFestivos(!openDialogFestivos);
  };
  //OPEN MODAL CREATE MATRICULAS
  const [openModalCreateMatriculasState, setOpenModalCreateMatriculasState] =
    useState(false);
  const openModalCreateMatriculas = () => {
    setOpenModalCreateMatriculasState(!openModalCreateMatriculasState);
  };
  //OPEN MODAL MOTIVOS
  const [openModalMotivos, setOpenModalMotivos] = useState(false);
  const openModalMotivosState = () => {
    setOpenModalMotivos(!openModalMotivos);
  };
  //STATE MODAL CREATE MOTIVOS
  const [openModalCreateMotivosState, setOpenModalCreateMotivosState] =
    useState(false);
  const openModalCreateMotivos = () => {
    setOpenModalCreateMotivosState(!openModalCreateMotivosState);
  };
  // Controlar Abre/Cierre del Alert para confirmación de eliminar
  const [openDelete, setOpenDelete] = useState(false);
  // Estado para el ID del gimnasio a eliminar
  const [courseIdToDelete, setCourseIdToDelete] = useState<number | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Función para abrir/cerrar el AlertErrorButtons
  const handleOpenDelete = (id: number | null) => {
    setCourseIdToDelete(id); // Establecemos el gimnasio a eliminar
    setOpenDelete(!openDelete); // Abrimos el AlertError
  };

  const handleDeleteCourse = async () => {
    try {
      await deleteCourse({ branchId: id, id: courseIdToDelete }).unwrap();
      toasts.success(
        "Curso eliminado",
        "El curso se ha eliminado correctamente"
      );
      setOpenDelete(false);
      coursesQuery.refetch();
    } catch (error) {
      toasts.danger(
        "Error al eliminar",
        "Ha ocurrido un error al eliminar el curso"
      );
      throw error;
    }
  };

  const handleCreateCourse = async (course: CourseValues) => {
    try {
      await createCourse({
        ...course,
        fee_ids: course.fee_ids.split(",").map((fee) => parseInt(fee)),
        branchId: id,
      }).unwrap();
      toasts.success("Curso creado", "El curso se ha creado correctamente");
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteCourseUser = async (userId: number) => {
    try {
      await deleteCourseUser({
        branchId: id,
        courseId: viewUserCourserId,
        userId,
      }).unwrap();

      // Primero refrescamos los datos
      await courseUsersQuery.refetch();

      // Luego mostramos el mensaje de éxito
      toasts.success(
        "Usuario eliminado",
        "El usuario se ha eliminado correctamente del curso"
      );

      // No cerramos el modal inmediatamente para que se vea la actualización
      setTimeout(() => {
        setOpenModalViewUserCourse(false);
        setViewUserCourserId(null);
      }, 500);
    } catch (error) {
      toasts.danger(
        "Error al eliminar",
        "Ha ocurrido un error al eliminar el usuario del curso"
      );
      throw error;
    }
  };

  const handleCreatePreviusAccessTime = async (values: any) => {
    try {
      await createPreviusAccessTime({
        time: values.course_time_access,
        branchId: id,
      }).unwrap();
      toasts.success(
        "Tiempo previo registrado",
        "El tiempo previo se ha registrado correctamente"
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  return (
    <div className="flex flex-col flex-1 overflow-y-scroll w-full pt-5">
      <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto">
        <TableListCourses
          isLoading={coursesQuery.isLoading}
          onSearchChange={(event) => setSearch(event.target.value)}
          onEditFee={(fee) => {
            setEditCurrentCourse(fee);
            setOpenDialogEditCourse(true);
          }}
          coachList={specialistsQuery.data ?? []}
          search={search}
          title={t("course.views.courses")}
          description={t("course.views.course_schedule")}
          fees={coursesQuery.data?.data ?? []}
          functionButtonCreate={openDialog}
          functionButtonMatriculas={functionOpenModalFestivos}
          functionOpenModalGrupos={functionOpenModalGrupos}
          functionButtonMotivos={openModalMotivosState}
          onViewUserCourse={(courseID) => {
            setViewUserCourserId(courseID as number);
            functionOpenModalViewUserCourse();
          }}
          pageSize={pageSize}
          currentPage={page}
          onDelete={handleOpenDelete}
        />
        {coursesQuery.data && (
          <PaginationUI
            onPageChange={(event) => handlePageChange(event)}
            currentPage={page}
            pageSize={10}
            totalResults={coursesQuery.data?.data?.length ?? 0}
            totalPages={Math.ceil(
              (coursesQuery?.data?.data?.length ?? 0) / pageSize
            )}
          />
        )}

        <AlertErrorButtons
          open={openDelete}
          onClose={() => handleOpenDelete(null)}
          title={t("course.views.courses")}
          message={t("course.views.delete_confirmation_message")}
          primaryButtonText={t("general.delete")}
          isLoading={deleteCourseResult.isLoading}
          secondaryButtonText={t("general.cancel")}
          onPrimaryButtonClick={handleDeleteCourse}
          onSecondaryButtonClick={() => handleOpenDelete(null)}
        />

        {/*DIALOG CREATE CURSOS*/}
        <Formik
          initialValues={courseInitialValues}
          validationSchema={courseValidationSchema}
          enableReinitialize
          onSubmit={async (values: CourseValues, formikHelpers) => {
            try {
              await handleCreateCourse(values);
              setOpen(false);
              formikHelpers.resetForm();
            } catch (error) {
              toasts.danger(
                "Error al crear curso",
                "Ha ocurrido un error al crear el curso"
              );
            }
          }}
        >
          {({
            handleChange,
            setFieldValue,
            values,
            handleSubmit,
            handleBlur,
            resetForm,
            errors,
            touched,
          }) => {
            useEffect(() => {
              if (specialistsQuery.isSuccess) {
                if (specialistsQuery?.data?.length > 0) {
                  setFieldValue("userId", specialistsQuery.data[0].id);
                }
              }
            }, [specialistsQuery]);

            return (
              <DialogCreate
                OnclickCreate={handleSubmit}
                OnclickCancel={() => {
                  setOpen(false);
                  resetForm();
                }}
                onClose={() => {
                  setOpen(false);
                  resetForm();
                }}
                tittleButtonCreate={t("general.create")}
                tittleButtonCancel={t("general.cancel")}
                open={open}
                setOpen={setOpen}
                tittle={t("course.views.course")}
                subTittle={t("course.views.create_course")}
              >
                <form>
                  <div className="mt-6 mx-2">
                    <div className="space-y-12">
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t(
                                "customer-to-gym-subscription.views.principal_caracteristics"
                              )}
                            </h2>
                          </div>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t(
                                "customer-to-gym-subscription.views.name"
                              )}
                              name="name"
                              span={t("general.mandatory")}
                              type="text"
                              placeholder={t(
                                "customer-to-gym-subscription.views.name"
                              )}
                              onChange={handleChange}
                              error={errors?.name && touched.name}
                              textError={errors?.name}
                              value={values.name}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t(
                                "customer-to-gym-subscription.views.description"
                              )}
                              name="description"
                              span={t("general.mandatory")}
                              error={errors?.description && touched.description}
                              textError={errors?.description}
                              onChange={handleChange}
                              value={values.description}
                              type="text"
                              placeholder={t(
                                "customer-to-gym-subscription.views.description"
                              )}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("lesson.views.capacity")}
                              span={t("general.mandatory")}
                              name="capacity"
                              type="number"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder={t("lesson.views.capacity")}
                              error={errors?.capacity && touched.capacity}
                              textError={errors?.capacity}
                              value={values.capacity}
                            />
                          </div>
                          <div className="col-span-full">
                            <Select
                              name="slot_place_id"
                              selected={slotsPlacesQuery?.data?.find(
                                (place) => place.id == values.slot_place_id
                              )}
                              setSelected={(option) =>
                                setFieldValue("slot_place_id", option.id)
                              }
                              options={slotsPlacesQuery.data ?? []}
                              tittle={t("slots.views.space")}
                            />
                          </div>
                          <div className="col-span-full">
                            <SelectAvatar
                              name="trainer"
                              span={t("general.mandatory")}
                              selected={specialistsQuery?.data?.find(
                                (user: any) => user.id == values.trainer
                              )}
                              setSelected={(selected) =>
                                setFieldValue("trainer", selected.id)
                              }
                              options={specialistsQuery?.data}
                              tittle={t("course.views.trainer")}
                            />
                            {errors?.trainer && touched?.trainer && (
                              <span className="text-red-500 text-sm">
                                {errors?.trainer as string}
                              </span>
                            )}
                          </div>
                          <div className="col-span-full">
                            {/* cuotas */}
                            <MultiSelect
                              span={t("general.optional")}
                              selectedOptions={coursesFeeQuery?.data?.filter(
                                (fee) =>
                                  typeof values.fee_ids === "string" &&
                                  values.fee_ids
                                    .split(",")
                                    .includes(fee.id + "")
                              )}
                              setSelectedOptions={(selected) =>
                                setFieldValue(
                                  "fee_ids",
                                  Array.isArray(selected)
                                    ? selected.map((fee) => fee.id).join(",")
                                    : selected.id
                                )
                              }
                              options={coursesFeeQuery?.data}
                              title={t("forms.label.fees")}
                              textDescribe={null}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t("lesson.views.calendaring")}
                            </h2>
                          </div>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full">
                            <CheckBoxDays
                              onChange={(days) => setFieldValue("days", days)}
                              value={values.days}
                              onBlur={handleBlur}
                              error={errors?.days && touched?.days}
                            />
                          </div>
                          <div className="col-span-full">
                            <DateRangePicker
                              //disabled={currentEventEdit && !values.all}
                              disabled={false}
                              span={t("general.mandatory")}
                              id="date"
                              label={t("lesson.views.interval_date")}
                              value={{
                                startDate: values.startDate
                                  ? DateTime.fromFormat(
                                      values.startDate,
                                      "yyyy-MM-dd"
                                    ).toJSDate()
                                  : new Date(),
                                endDate: values.endDate
                                  ? DateTime.fromFormat(
                                      values.endDate,
                                      "yyyy-MM-dd"
                                    ).toJSDate()
                                  : new Date(),
                              }}
                              onDateChange={(startDate, endDate) => {
                                setFieldValue(
                                  "startDate",
                                  DateTime.fromJSDate(startDate).toFormat(
                                    "yyyy-MM-dd"
                                  )
                                );
                                setFieldValue(
                                  "endDate",
                                  DateTime.fromJSDate(endDate).toFormat(
                                    "yyyy-MM-dd"
                                  )
                                );
                              }}
                            />
                            {errors?.startDate && touched?.startDate && (
                              <span className="text-red-500 text-sm">
                                {errors?.startDate as string}
                              </span>
                            )}
                          </div>
                          <div className="col-span-full grid grid-cols-2 gap-8 place-content-center">
                            <InputWithCornerHint
                              label={t("lesson.views.time_init")}
                              span={t("general.mandatory")}
                              name="startTime"
                              type="time"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.startTime}
                              error={touched.startTime && !!errors.startTime}
                              textError={touched.startTime && errors.startTime}
                            />
                            <InputWithCornerHint
                              label={t("lesson.views.duration")}
                              span={t("general.mandatory")}
                              name="duration"
                              type="number"
                              value={values.duration}
                              error={touched.duration && !!errors.duration}
                              textError={touched.duration && errors.duration}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                          {currentEventEdit && (
                            <div className="col-span-full">
                              <div className="relative flex items-start">
                                <div className="flex h-6 items-center">
                                  <input
                                    checked={values.all}
                                    onChange={(event) =>
                                      setFieldValue("all", event.target.checked)
                                    }
                                    name="all"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                                    onBlur={handleBlur}
                                  />
                                </div>
                                <div className="ml-3 text-sm leading-6">
                                  <label
                                    htmlFor=""
                                    className="font-medium text-gray-900"
                                  >
                                    Este evento y los posteriores
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </DialogCreate>
            );
          }}
        </Formik>

        {/*DIALOG EDIT CURSO*/}
        <Formik
          initialValues={{
            ...editCurrentCourse,
            startTime: editCurrentCourse?.time_start,
            startDate: editCurrentCourse?.date_start,
            endDate: editCurrentCourse?.date_end,
            trainer: editCurrentCourse?.coach_id,
            days: editCurrentCourse?.days.split(", "),
            fee_ids: editCurrentCourse?.fees,
          }}
          validationSchema={courseValidationSchema}
          enableReinitialize
          onSubmit={async (values, formikHelpers) => {
            await updateCourse({
              ...values,
              coach: values.trainer,
              date_start: DateTime.fromISO(values.startDate).toFormat(
                "dd/LL/yyyy"
              ),
              date_end: DateTime.fromISO(values.endDate).toFormat("dd/LL/yyyy"),
              branchId: id,
              day: values.days,
              fee_ids: values.fee_ids.split(",").map((fee) => parseInt(fee)),
              id: editCurrentCourse.id,
              time_start: values.startTime,
            }).unwrap();
            setEditCurrentCourse(undefined);
            setOpenDialogEditCourse(false);
            formikHelpers.resetForm();
          }}
        >
          {({
            handleChange,
            setFieldValue,
            values,
            handleSubmit,
            handleBlur,
            resetForm,
            errors,
            touched,
          }) => {
            useEffect(() => {
              if (specialistsQuery.isSuccess) {
                if (specialistsQuery?.data?.length > 0) {
                  const coach = specialistsQuery.data.find(
                    (coach) => coach.id === values?.coach_id
                  );

                  setFieldValue("coach_id", coach?.id);
                }
              }
            }, [specialistsQuery, values?.coach_id]);

            console.log("values", editCurrentCourse);

            return (
              <DialogCreate
                OnclickCreate={handleSubmit}
                OnclickCancel={() => {
                  setOpenDialogEditCourse(false);
                  setEditCurrentCourse(undefined);
                  resetForm();
                }}
                onClose={() => {
                  setOpenDialogEditCourse(false);
                  setEditCurrentCourse(undefined);
                  resetForm();
                }}
                tittleButtonCreate={t("general.edit")}
                tittleButtonCancel={t("general.cancel")}
                isLoading={updateCourseResult.isLoading}
                acceptDisabled={updateCourseResult.isLoading}
                open={openDialogEditCourse}
                setOpen={setOpenDialogEditCourse}
                tittle={t("course.views.course")}
                subTittle={t("course.views.edit_course")}
              >
                <form>
                  <div className="mt-6 mx-2">
                    <div className="space-y-12">
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t(
                                "customer-to-gym-subscription.views.principal_caracteristics"
                              )}
                            </h2>
                          </div>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t(
                                "customer-to-gym-subscription.views.name"
                              )}
                              name="name"
                              span={t("general.mandatory")}
                              type="text"
                              placeholder={t(
                                "customer-to-gym-subscription.views.name"
                              )}
                              onChange={handleChange}
                              error={errors?.name && touched.name}
                              textError={errors?.name}
                              value={values?.name}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t(
                                "customer-to-gym-subscription.views.description"
                              )}
                              name="description"
                              span={t("general.mandatory")}
                              error={errors?.description && touched.description}
                              textError={errors?.description}
                              onChange={handleChange}
                              value={values?.description}
                              type="text"
                              placeholder={t(
                                "customer-to-gym-subscription.views.description"
                              )}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("lesson.views.capacity")}
                              span={t("general.mandatory")}
                              name="capacity"
                              type="number"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder={t("lesson.views.capacity")}
                              error={errors?.capacity && touched.capacity}
                              textError={errors?.capacity}
                              value={values?.capacity}
                            />
                          </div>

                          <div className="col-span-full">
                            <Select
                              name="slot_place_id"
                              selected={slotsPlacesQuery?.data?.find(
                                (place) => place.id == values?.slot_place_id
                              )}
                              setSelected={(option) =>
                                setFieldValue("slot_place_id", option.id)
                              }
                              options={slotsPlacesQuery.data ?? []}
                              tittle={t("slots.views.space")}
                            />
                          </div>

                          <div className="col-span-full">
                            <SelectAvatar
                              name="trainer"
                              span={t("general.mandatory")}
                              selected={specialistsQuery?.data?.find(
                                (user: any) => user.id == values?.trainer
                              )}
                              setSelected={(selected) =>
                                setFieldValue("trainer", selected.id)
                              }
                              options={specialistsQuery?.data}
                              tittle={t("course.views.trainer")}
                            />
                            {errors?.coach_id && touched?.trainer && (
                              <span className="text-red-500 text-sm">
                                {errors?.trainer as string}
                              </span>
                            )}
                          </div>

                          <div className="col-span-full">
                            {/* cuotas */}
                            <MultiSelect
                              span={t("general.optional")}
                              selectedOptions={coursesFeeQuery?.data?.filter(
                                (fee) =>
                                  typeof values.fee_ids === "string" &&
                                  values.fee_ids
                                    .split(",")
                                    .includes(fee.id + "")
                              )}
                              setSelectedOptions={(selected) =>
                                setFieldValue(
                                  "fee_ids",
                                  Array.isArray(selected)
                                    ? selected.map((fee) => fee.id).join(",")
                                    : selected.id
                                )
                              }
                              options={coursesFeeQuery?.data}
                              title={t("forms.label.fees")}
                              textDescribe={null}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t("lesson.views.calendaring")}
                            </h2>
                          </div>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full">
                            <CheckBoxDays
                              onChange={(days) => setFieldValue("days", days)}
                              value={values?.days}
                              onBlur={handleBlur}
                              error={errors?.days && touched?.days}
                            />
                          </div>
                          <div className="col-span-full">
                            <DateRangePicker
                              //disabled={currentEventEdit && !values.all}
                              disabled={false}
                              span={t("general.mandatory")}
                              id="date"
                              label={t("lesson.views.interval_date")}
                              value={{
                                startDate: values?.startDate
                                  ? DateTime.fromFormat(
                                      values?.startDate,
                                      "yyyy-MM-dd"
                                    ).toJSDate()
                                  : new Date(),
                                endDate: values?.endDate
                                  ? DateTime.fromFormat(
                                      values?.endDate,
                                      "yyyy-MM-dd"
                                    ).toJSDate()
                                  : new Date(),
                              }}
                              onDateChange={(startDate, endDate) => {
                                setFieldValue(
                                  "startDate",
                                  DateTime.fromJSDate(startDate).toFormat(
                                    "yyyy-MM-dd"
                                  )
                                );
                                setFieldValue(
                                  "endDate",
                                  DateTime.fromJSDate(endDate).toFormat(
                                    "yyyy-MM-dd"
                                  )
                                );
                              }}
                            />
                            {errors?.startDate && touched?.startDate && (
                              <span className="text-red-500 text-sm">
                                {errors?.startDate as string}
                              </span>
                            )}
                          </div>
                          <div className="col-span-full grid grid-cols-2 gap-8 place-content-center">
                            <InputWithCornerHint
                              label={t("lesson.views.time_init")}
                              span={t("general.mandatory")}
                              name="startTime"
                              type="time"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values?.startTime}
                              error={errors?.startTime && touched?.startTime}
                              textError={errors?.startTime}
                            />
                            <InputWithCornerHint
                              label={t("lesson.views.duration")}
                              span={t("general.mandatory")}
                              name="duration"
                              type="number"
                              value={values?.duration}
                              error={errors?.duration && touched?.duration}
                              textError={errors?.duration}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                          {/* {editCurrentCourse && (
                            <div className="col-span-full">
                              <div className="relative flex items-start">
                                <div className="flex h-6 items-center">
                                  <input
                                    checked={values?.all}
                                    onChange={(event) =>
                                      setFieldValue("all", event.target.checked)
                                    }
                                    name="all"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                                    onBlur={handleBlur}
                                  />
                                </div>
                                <div className="ml-3 text-sm leading-6">
                                  <label
                                    htmlFor=""
                                    className="font-medium text-gray-900"
                                  >
                                    Este evento y los posteriores
                                  </label>
                                </div>
                              </div>
                            </div>
                          )} */}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </DialogCreate>
            );
          }}
        </Formik>

        {/*MODAL TABLE FESTIVOS*/}
        <ModalXXL
          onClickCreate={() => {}}
          Close={functionOpenModalFestivos}
          open={openModalFestivos}
          tittleModal={t("lesson.views.festivos")}
          functionButtonTopCreate={functionOpenDialogFestivos}
          textButtonClose={t("general.close")}
          modalClassName="z-50"
        >
          <TableListFestivos
            openModal={functionOpenDialogFestivos}
            optionsFestivos={holidaysQuery.data}
            onDelete={(holiday) => deleteHoliday(holiday)}
            onEdit={(holiday) =>
              setCurrentHolidayEdit({
                ...holiday,
                time_start: holiday.timeStart,
                time_end: holiday.timeEnd,
              })
            }
          />
        </ModalXXL>
        {/*FIN MODAL TABLE FESTIVOS*/}

        {/*DIALOG FESTIVOS*/}
        <Formik
          enableReinitialize
          initialValues={currentHolidayEdit ?? holidaysInitialValues}
          validationSchema={holidaysValidationSchema}
          onSubmit={(values, formikHelpers) => {
            if (currentHolidayEdit) {
              updateHoliday({
                ...values,
                branchId: id,
              });
            } else {
              createHoliday({
                ...values,
                branchId: id,
              });
            }

            formikHelpers.resetForm();
          }}
        >
          {({
            handleChange,
            setFieldValue,
            handleBlur,
            handleSubmit,
            values,
            errors,
            touched,
          }) => (
            <DialogCreate
              onClose={() => setCurrentHolidayEdit(null)}
              open={openDialogFestivos}
              setOpen={functionOpenDialogFestivos}
              tittle={t("lesson.views.festivos")}
              subTittle={t("lesson.views.creation_festivos")}
              tittleButtonCreate={
                currentHolidayEdit ? t("general.edit") : t("general.create")
              }
              tittleButtonCancel={t("general.cancel")}
              OnclickCreate={handleSubmit}
              OnclickCancel={functionOpenDialogFestivos}
            >
              <form onSubmit={handleSubmit}>
                <div className="mt-6 mx-2">
                  <div className="space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                      <div className="flex items-center justify-center h-full text-center">
                        <div>
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            {t("lesson.views.caracteristics")}
                          </h2>
                        </div>
                      </div>
                      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("lesson.views.festivo")}
                            span={t("general.mandatory")}
                            id="description"
                            name="description"
                            type="text"
                            placeholder={t("lesson.views.festivo")}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors?.description && touched?.description}
                            textError={
                              errors?.description && touched?.description
                            }
                            value={values?.description}
                          />
                        </div>
                        <div className="col-span-full">
                          <Select
                            name="type"
                            selected={optionSelectFestivos.find(
                              (festivo) => festivo.id == values.type
                            )}
                            setSelected={(selected) =>
                              setFieldValue("type", selected.id)
                            }
                            options={optionSelectFestivos}
                            tittle={t("lesson.views.type")}
                            span={t("general.mandatory")}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                      <div className="flex items-center justify-center h-full text-center">
                        <div>
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            {t("lesson.views.calendaring")}
                          </h2>
                        </div>
                      </div>
                      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("lesson.views.date")}
                            span={t("general.mandatory")}
                            id="date"
                            name="date"
                            type="date"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.date && !!errors.date}
                            textError={touched.date && errors.date}
                            value={values.date}
                          />
                        </div>
                        <div className="col-span-full grid grid-cols-2 gap-8 place-content-center">
                          <InputWithCornerHint
                            label={t("lesson.views.time_init")}
                            span={t("general.mandatory")}
                            id="time_start"
                            name="time_start"
                            type="time"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.time_start && !!errors.time_start}
                            textError={errors.time_start}
                            value={values.time_start}
                          />
                          <InputWithCornerHint
                            label={t("lesson.views.time_end")}
                            span={t("general.mandatory")}
                            id="time_end"
                            name="time_end"
                            type="time"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.time_end && !!errors.time_end}
                            textError={errors.time_end}
                            value={values.time_end}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </DialogCreate>
          )}
        </Formik>
        {/*FIN DIALOG FESTIVOS*/}

        {/* Modal matriculas table*/}
        <ModalXXL
          open={openModal}
          Close={openModalMatriculas}
          tittleModal={t("customer-to-gym-subscription.views.registrations")}
          textButtonClose={t("general.close")}
          textButtonCreate={t(
            "customer-to-gym-subscription.views.create_registration"
          )}
          functionButtonTopCreate={openModalCreateMatriculas}
        >
          <TableListMatricula
            onEdit={(registrationPayment) =>
              setCurrentRegistrationPaymentEdit(registrationPayment)
            }
            registrationPayments={registrationPaymentsQuery?.data}
          />
        </ModalXXL>

        {/* Modal matriculas create*/}
        <Formik
          enableReinitialize
          validationSchema={createRegistrationPaymentValidationSchema}
          initialValues={
            currentRegistrationPaymentEdit ??
            createRegistrationPaymentInitialValues
          }
          onSubmit={(values, formikHelpers) => {
            if (currentRegistrationPaymentEdit) {
              updateRegistrationPayment({
                ...values,
                branchId: id,
                id: currentRegistrationPaymentEdit?.id,
              });
            } else {
              createRegistrationPayment({ ...values, branchId: id });
            }
            setCurrentRegistrationPaymentEdit(undefined);
            openModalCreateMatriculas();
            formikHelpers.resetForm();
          }}
        >
          {({ handleChange, values, errors, handleSubmit }) => (
            <Modal
              open={openModalCreateMatriculasState}
              Close={openModalCreateMatriculas}
              tittleModal={t("customer-to-gym-subscription.views.registration")}
              onClickCreate={handleSubmit}
              textButtonCreate={t("general.create")}
              textButtonClose={t("general.cancel")}
            >
              <div className="col-span-full mt-5">
                <InputWithCornerHint
                  label={t("customer-to-gym-subscription.views.name")}
                  name="name"
                  type="text"
                  placeholder={t("customer-to-gym-subscription.views.name")}
                  onChange={handleChange}
                  error={!!errors.name}
                  textError={errors.name}
                  value={values.name}
                />
              </div>
              <div className="col-span-full mt-5">
                <InputDropDownSpan
                  label={t("customer-to-gym-subscription.views.mount")}
                  id=""
                  name="price"
                  type="number"
                  value={values.price}
                  selectValue={values.currency}
                  onSelectChange={handleChange}
                  onChange={handleChange}
                  placeholder={t("customer-to-gym-subscription.views.mount")}
                  idSelect=""
                  labelSelect=""
                  nameSelect="currency"
                  options={currencies}
                />
              </div>
            </Modal>
          )}
        </Formik>

        {/* Modal tiempos previos vista Curso*/}
        <Formik
          initialValues={{
            course_time_access: 0,
          }}
          validationSchema={Yup.object({
            course_time_access: Yup.number().required(FIELD_REQUIRED_MESSAGE),
          })}
          onSubmit={async (values, formikHelpers) => {
            try {
              await handleCreatePreviusAccessTime(values);
              formikHelpers.resetForm();
              openModalMotivosState();
            } catch (error) {
              console.error(error);
            }
          }}
        >
          {({ handleChange, values, errors, touched, handleSubmit }) => (
            <Modal
              open={openModalMotivos}
              Close={openModalMotivosState}
              tittleModal={t("lesson.views.hours")}
              textButtonCreate={t("general.accept")}
              textButtonClose={t("general.cancel")}
              onClickCreate={handleSubmit}
              loader={createPreviusAccessTimeResult.isLoading}
              modalClassName="z-50"
            >
              <div className="col-span-full mt-5">
                <InputWithCornerHint
                  label={t("lesson.views.previus_access_time")}
                  name="course_time_access"
                  id="course_time_access"
                  type="number"
                  placeholder={t("lesson.views.previus_access_time")}
                  onChange={handleChange}
                  error={
                    errors.course_time_access && touched.course_time_access
                  }
                  textError={errors.course_time_access}
                  value={values.course_time_access}
                />
              </div>
            </Modal>
          )}
        </Formik>

        {/* Modal MOTIVOS TABS*/}
        <Formik
          initialValues={createReasonInitialValue}
          validationSchema={createReasonValidationSchema}
          onSubmit={(values: CreateReasonValues, formikHelpers) => {
            createReason({ ...values, branchId: id });
            formikHelpers.resetForm();
          }}
        >
          {({
            handleChange,
            values,
            errors,
            setFieldValue,
            touched,
            handleSubmit,
          }) => (
            <Modal
              open={openModalCreateMotivosState}
              Close={openModalCreateMotivos}
              textButtonCreate={t("general.accept")}
              textButtonClose={t("general.cancel")}
              onClickCreate={handleSubmit}
              tittleModal={t(
                "customer-to-gym-subscription.views.create_reason"
              )}
            >
              <div className="col-span-full mt-5">
                <InputWithCornerHint
                  label={t("customer-to-gym-subscription.views.motive")}
                  name="description"
                  type="text"
                  placeholder={t("customer-to-gym-subscription.views.motive")}
                  onChange={handleChange}
                  error={errors?.description && touched?.description}
                  value={values?.description}
                />
              </div>
              <div className="col-span-full mt-5">
                <Select
                  name="type"
                  selected={reasonTypes.find(
                    (reasonType) => reasonType.value == values.type
                  )}
                  setSelected={(option: any) =>
                    setFieldValue("type", option?.value)
                  }
                  options={reasonTypes}
                  tittle={t(
                    "customer-to-gym-subscription.views.description_reason"
                  )}
                />
              </div>
            </Modal>
          )}
        </Formik>

        {/*MODAL GRUPOS*/}
        <ModalXXL
          onClickCreate={() => {}}
          Close={functionOpenModalGrupos}
          open={openModalGrupos}
          tittleModal={t("lesson.views.groups")}
          functionButtonTopCreate={functionOpenModalCreateGrupos}
          textButtonClose={t("general.close")}
          modalClassName="z-50"
        >
          <TableListGrupos
            editGroups={(group) => {
              functionOpenModalCreateGrupos();
              setCurrentGroupEdit(group);
            }}
            onDelete={(groupID) => {
              handleOpenDeleteGroup(groupID);
            }}
            optionsGroup={listGroupsQuery.data ?? []}
          />
        </ModalXXL>
        {/*FIN MODAL GRUPOS*/}

        {/*DIALOG CREATE/EDIT GRUPOS*/}
        <Formik
          initialValues={
            currentGroupEdit
              ? {
                  ...currentGroupEdit,
                  description: currentGroupEdit?.description,
                  color: currentGroupEdit?.color?.toUpperCase(),
                  slots_from_group: currentGroupEdit?.places?.map(
                    (place) => place.name
                  ),
                  slot_input: "",
                  name: currentGroupEdit?.name,
                }
              : {
                  name: "",
                  description: "",
                  color: "#A7BED3",
                  slots_from_group: [],
                  slot_input: "",
                }
          }
          enableReinitialize
          validationSchema={Yup.object({
            name: Yup.string().required(FIELD_REQUIRED_MESSAGE),
            description: Yup.string().required(FIELD_REQUIRED_MESSAGE),
            color: Yup.string().required(FIELD_REQUIRED_MESSAGE),
            slots_from_group: Yup.array().of(Yup.string().optional()),
            slot_input: Yup.string().optional(),
          })}
          onSubmit={async (values, formikHelpers) => {
            try {
              const finalValues = {
                name: values.name,
                description: values.description,
                color: values.color,
                places: values?.slot_input.trim()
                  ? [
                      ...(values?.slots_from_group || []),
                      values?.slot_input.trim(),
                    ]
                  : values?.slots_from_group,
              };

              if (currentGroupEdit) {
                await updateGroup({
                  ...finalValues,
                  branchId: id,
                  slot_group_id: currentGroupEdit.id,
                }).unwrap();
                toasts.success(
                  "Actualización exitosa",
                  "El grupo se ha actualizado correctamente"
                );
                listGroupsQuery.refetch();
                slotsPlacesQuery.refetch();
                setCurrentGroupEdit(undefined);
              } else {
                await createGroup({ ...finalValues, branchId: id }).unwrap();
                toasts.success(
                  "Registro exitoso",
                  "El grupo se ha registrado correctamente"
                );
                listGroupsQuery.refetch();
                slotsPlacesQuery.refetch();
                setCurrentGroupEdit(undefined);
              }
              formikHelpers.resetForm();
              functionOpenModalCreateGrupos();
            } catch (error) {
              console.error(error);
              toasts.danger(
                "Error al registrar",
                "Ha ocurrido un error al registrar el grupo"
              );
            }
          }}
        >
          {({
            handleChange,
            values,
            errors,
            touched,
            resetForm,
            setFieldValue,
            handleSubmit,
          }) => (
            <DialogCreate
              open={openModalCreateGrupos}
              setOpen={() => {
                functionOpenModalCreateGrupos();
                resetForm();
              }}
              tittle={"Crea un grupo de espacios"}
              subTittle={null}
              tittleButtonCreate={t("general.save")}
              tittleButtonCancel={t("general.cancel")}
              onClose={() => {
                functionOpenModalCreateGrupos();
                resetForm();
              }}
              OnclickCreate={handleSubmit}
              isLoading={createGroupResult.isLoading}
              acceptDisabled={createGroupResult.isLoading}
              OnclickCancel={() => {
                functionOpenModalCreateGrupos();
                resetForm();
              }}
            >
              <div>
                <div className="mt-6 mx-2">
                  <div className="space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                      <div className="flex items-center justify-center h-full text-center">
                        <div>
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            {t("lesson.views.groups")}
                          </h2>
                        </div>
                      </div>
                      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("lesson.views.name")}
                            span={t("general.mandatory")}
                            id="name"
                            name="name"
                            type="text"
                            placeholder={t("lesson.views.name")}
                            onChange={handleChange}
                            value={values?.name}
                            error={errors?.name && touched?.name}
                            textError={errors?.name}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("lesson.views.description")}
                            span={t("general.mandatory")}
                            id="description"
                            name="description"
                            type="text"
                            placeholder={t("lesson.views.description")}
                            onChange={handleChange}
                            value={values?.description}
                            error={errors?.description && touched?.description}
                            textError={errors?.description}
                          />
                        </div>
                        <div className="col-span-full">
                          <SelectColor
                            options={colors}
                            span={null}
                            tittle={t("lesson.views.color")}
                            selected={colors.find(
                              (option) => option?.color == values?.color
                            )}
                            setSelected={(selected) => {
                              setFieldValue("color", selected?.color);
                            }}
                          />
                        </div>
                        <div className="col-span-full">
                          <div className="flex items-center gap-2">
                            <InputWithCornerHint
                              type="text"
                              id="slot_input"
                              label={t("slots.views.slots_from_group")}
                              name="slot_input"
                              placeholder="Nuevo espacio"
                              value={values.slot_input}
                              onChange={(e) =>
                                setFieldValue("slot_input", e.target.value)
                              }
                              className="flex-1"
                            />
                            <button
                              type="button"
                              className="bg-gray-300 mt-6 text-white p-2 rounded"
                              onClick={() => {
                                if (values.slot_input.trim() !== "") {
                                  setFieldValue("slots_from_group", [
                                    ...values.slots_from_group,
                                    values.slot_input.trim(),
                                  ]);
                                  setFieldValue("slot_input", ""); // limpiar input
                                }
                              }}
                            >
                              <PlusCircleIcon
                                aria-hidden="true"
                                className="h-5 w-5"
                              />
                            </button>
                          </div>

                          {values?.slots_from_group?.map((slot, idx) => (
                            <div
                              key={idx}
                              className="flex items-center mt-3 justify-between bg-gray-100 px-4 py-2 mb-2"
                            >
                              <span>{slot}</span>
                              <button
                                type="button"
                                className="text-white p-2 rounded"
                                onClick={() => {
                                  const updated =
                                    values.slots_from_group.filter(
                                      (_, i) => i !== idx
                                    );
                                  setFieldValue("slots_from_group", updated);
                                }}
                              >
                                <Trash2Icon className="h-5 w-5 text-red-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DialogCreate>
          )}
        </Formik>
        {/*DIALOG CREATE/EDIT GRUPOS */}

        <AlertErrorButtons
          open={openDeleteGroup}
          onClose={() => setOpenDeleteGroup(false)}
          title={"Eliminar grupo"}
          message={
            "¿Confirmas que quieres eliminar este grupo de espacio? Ten en cuenta que no se borrará ninguno de los eventos ya creados."
          }
          primaryButtonText={t("general.delete")}
          secondaryButtonText={t("general.cancel")}
          onPrimaryButtonClick={async () => {
            try {
              await deleteGroup({
                branchId: id,
                id: currentGroupToDeleteId,
              }).unwrap();
              toasts.success(
                "Grupo eliminado",
                "El grupo se ha eliminado correctamente"
              );
              listGroupsQuery.refetch();
              setOpenDeleteGroup(false);
              setCurrentGroupToDeleteId(null);
            } catch (error) {
              toasts.danger(
                "Error al eliminar",
                "Ha ocurrido un error al eliminar el grupo"
              );
              console.error(error);
            }
          }}
          onSecondaryButtonClick={() => setOpenDeleteGroup(false)}
          isLoading={deleteGroupResult.isLoading}
        />

        {/*DIALOG CREATE MOTIVOS*/}
        <DialogCreate
          open={openDialogCreateGrupos}
          setOpen={functionOpenDialogCreateGrupos}
          tittle={t("course.views.group")}
          subTittle={t("lesson.views.edit_group")}
          tittleButtonCreate={t("general.edit")}
          tittleButtonCancel={t("general.cancel")}
          OnclickCreate={() => {}}
          OnclickCancel={functionOpenDialogCreateGrupos}
        >
          <form action="">
            <div className="mt-6 mx-2">
              <div className="space-y-12">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <h2 className="text-base font-semibold leading-7 text-gray-900">
                        {t("lesson.views.groups")}
                      </h2>
                    </div>
                  </div>
                  <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                    <div className="col-span-full">
                      <InputFile
                        name="file-upload"
                        id="file-upload"
                        label={t("lesson.views.thumbnail")}
                        span=""
                        spanButton={t("general.up_image")}
                        description={t("general.format_image")}
                        selectedImage={""}
                        error={false}
                        onFileChange={() => {}}
                        onRemoveImage={() => {}}
                        tittleDelete={t("general.delete_image")}
                      />
                      <p className="text-sm text-gray-400 mt-1 text-center">
                        {t("lesson.views.thumbnail_description")}
                      </p>
                    </div>
                    <div className="col-span-full">
                      <InputWithCornerHint
                        label={t("lesson.views.description")}
                        span=""
                        id=""
                        name=""
                        type="text"
                        placeholder={t("lesson.views.description")}
                        onChange={() => {}}
                        error={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </DialogCreate>
        {/*DIALOG EDIT GRUPOS */}

        {/*MODAL VIEW USER COURSE*/}
        <ModalXXL
          open={openModalViewUserCourse}
          Close={() => {
            functionOpenModalViewUserCourse();
            setViewUserCourserId(null);
          }}
          tittleModal={t("lesson.views.users")}
          textButtonClose={t("general.close")}
        >
          <TableListUserCourse
            users={courseUsersQuery?.data?.data ?? []}
            courseDetails={courseUsersQuery?.data?.course ?? {}}
            onClickEmpty={null}
            onDeleteUser={async (userID) => {
              try {
                await handleDeleteCourseUser(userID);
              } catch (error) {
                console.error(error);
              }
            }}
          />
        </ModalXXL>
      </div>
    </div>
  );
};

const TableListUserCourse = ({
  users,
  onClickEmpty,
  courseDetails,
  onDeleteUser,
}) => {
  return (
    <div className="mt-2">
      <div className="sm:flex sm:items-center justify-between">
        <div className="flex-grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {courseDetails?.name}
          </h1>
        </div>

        <div>
          <span>
            {courseDetails?.registered_users?.length}/{courseDetails?.capacity}
          </span>
        </div>
      </div>
      <div className="border-b border-gray-300 mt-5"></div>
      <div className="flow-root">
        <div className="overflow-x-auto max-h-[60vh]">
          <div className="inline-block min-w-full align-middle">
            {users.length === 0 ? (
              <Empty
                onClick={onClickEmpty}
                texto={t("lesson.views.no_users")}
              />
            ) : (
              <div className="grid mt-4 grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {users.map((user) => (
                  <CardUserCourse
                    user={user?.user}
                    onDeleteUser={onDeleteUser}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CardUserCourse = ({ user, onDeleteUser }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-3 relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          onDeleteUser(user.id);
        }}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
      >
        <Trash2Icon />
      </button>
      <div className="pr-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {user.name}
        </h3>
        <p className="text-sm text-gray-600">{user.email}</p>
      </div>
    </div>
  );
};
